import React from "react"
import axios from "axios"
import { toast } from "react-toastify"
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'

const ProfileScreen = () => {
  const [profileData, setProfileData] = React.useState({
    name: "",
    last_name: "",
    email: "",
    phone: "",
    next_of_kin_first_name: "",
    next_of_kin_last_name: "",
    next_of_kin_phone: "",
    next_of_kin_email: "",
  })
  const [errors, setErrors] = React.useState({})
  const [isLoading, setIsLoading] = React.useState(true)
  const [isSaving, setIsSaving] = React.useState(false)

  // Fetch user profile data
  React.useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/profile`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("auth_key")}`,
            },
          }
        )
        
        if (response.data) {
          setProfileData(response.data)
        }
      } catch (error) {
        console.error("Error fetching profile:", error)
        if (error.response?.status === 401 || error.response?.status === 404) {
          toast.error("Session expired, please log in again", {
            position: "top-right",
            autoClose: 3000,
          })
          setTimeout(() => {
            localStorage.removeItem("auth_key")
            window.location.href = "/"
          }, 1000)
        } else {
          toast.error("Failed to load profile data", {
            position: "top-right",
            autoClose: 3000,
          })
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchProfile()
  }, [])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setProfileData((prev) => ({
      ...prev,
      [name]: value,
    }))
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }))
    }
  }

  const handlePhoneChange = (value, fieldName) => {
    setProfileData((prev) => ({
      ...prev,
      [fieldName]: value || "",
    }))
    
    // Clear error for this field when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ""
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}
    
    // Name is required
    if (!profileData.name || profileData.name.trim() === '') {
      newErrors.name = 'First Name is required'
    }
    
    // Email validation for next of kin if provided
    if (profileData.next_of_kin_email && profileData.next_of_kin_email.trim() !== '') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(profileData.next_of_kin_email)) {
        newErrors.next_of_kin_email = 'Please enter a valid email address'
      }
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validate form before submitting
    if (!validateForm()) {
      toast.error("Please fix the errors before saving", {
        position: "top-right",
        autoClose: 3000,
      })
      return
    }
    
    setIsSaving(true)

    try {
      let firstName = (profileData.name || "").trim()
      let lastName = (profileData.last_name || "").trim()

      if (!lastName && firstName.includes(" ")) {
        const [first, ...rest] = firstName.split(/\s+/)
        firstName = first
        lastName = rest.join(" ").trim()
      }

      const payload = {
        ...profileData,
        name: firstName,
        last_name: lastName,
      }

      await axios.put(
        `${process.env.REACT_APP_BASE_URL}/profile`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth_key")}`,
          },
        }
      )

      toast.success("Profile updated successfully!", {
        position: "top-right",
        autoClose: 3000,
      })
      if (firstName) localStorage.setItem("user_first_name", firstName)
      if (lastName) localStorage.setItem("user_last_name", lastName)
      if (profileData.email)
        localStorage.setItem("user_email", profileData.email)
    } catch (error) {
      console.error("Error updating profile:", error)
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error("Unauthorized access", {
          position: "top-right",
          autoClose: 3000,
        })
        setTimeout(() => {
          localStorage.removeItem("auth_key")
          window.location.href = "/"
        }, 1000)
      } else if (error.response?.status === 400) {
        // Handle validation errors from backend
        toast.error(error.response.data.error || "Validation error", {
          position: "top-right",
          autoClose: 3000,
        })
      } else {
        toast.error("Failed to update profile", {
          position: "top-right",
          autoClose: 3000,
        })
      }
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="dashboard-content">
        <div className="profile-container">
          <div className="profile-header">
            <h1 className="profile-title">Profile</h1>
            <div className="profile-subtitle">
              Manage your personal and next-of-kin information.
            </div>
          </div>
          <div className="profile-form">
            <div className="loading-skeleton">
              <div className="skeleton-line" />
              <div className="skeleton-line" />
              <div className="skeleton-line" />
              <div className="skeleton-line" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="dashboard-content">
      <div className="profile-container">
        <div className="profile-header">
          <h1 className="profile-title">Profile</h1>
          <div className="profile-subtitle">
            Manage your personal and next-of-kin information.
          </div>
        </div>

        <form className="profile-form" onSubmit={handleSubmit}>
          {/* Personal Information Section */}
          <div className="form-section">
            <h2 className="form-section-title">Personal Information</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  First Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={profileData.name || ""}
                  onChange={handleInputChange}
                  className={`form-input ${errors.name ? 'form-input-error' : ''}`}
                  placeholder="Enter your first name"
                />
                {errors.name && <div className="form-error">{errors.name}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="last_name" className="form-label">
                  Last Name
                </label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={profileData.last_name || ""}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Enter your last name"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={profileData.email || ""}
                className="form-input form-input-disabled"
                disabled
                placeholder="Your email address"
              />
              <div className="form-help">
                Email cannot be changed as it's used for authentication.
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="phone" className="form-label">
                Phone Number
              </label>
              <PhoneInput
                international
                defaultCountry="NG"
                value={profileData.phone || ""}
                onChange={(value) => handlePhoneChange(value, 'phone')}
                className={`phone-input-container ${errors.phone ? 'form-input-error' : ''}`}
                placeholder="Enter phone number"
              />
              {errors.phone && <div className="form-error">{errors.phone}</div>}
            </div>
          </div>

          {/* Next of Kin Section */}
          <div className="form-section">
            <h2 className="form-section-title">Next of Kin</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="next_of_kin_first_name" className="form-label">
                  First Name
                </label>
                <input
                  type="text"
                  id="next_of_kin_first_name"
                  name="next_of_kin_first_name"
                  value={profileData.next_of_kin_first_name || ""}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Enter next of kin first name"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="next_of_kin_last_name" className="form-label">
                  Last Name
                </label>
                <input
                  type="text"
                  id="next_of_kin_last_name"
                  name="next_of_kin_last_name"
                  value={profileData.next_of_kin_last_name || ""}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Enter next of kin last name"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="next_of_kin_phone" className="form-label">
                Phone Number
              </label>
              <PhoneInput
                international
                defaultCountry="NG"
                value={profileData.next_of_kin_phone || ""}
                onChange={(value) => handlePhoneChange(value, 'next_of_kin_phone')}
                className={`phone-input-container ${errors.next_of_kin_phone ? 'form-input-error' : ''}`}
                placeholder="Enter phone number"
              />
              {errors.next_of_kin_phone && <div className="form-error">{errors.next_of_kin_phone}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="next_of_kin_email" className="form-label">
                Email
              </label>
              <input
                type="email"
                id="next_of_kin_email"
                name="next_of_kin_email"
                value={profileData.next_of_kin_email || ""}
                onChange={handleInputChange}
                className={`form-input ${errors.next_of_kin_email ? 'form-input-error' : ''}`}
                placeholder="Enter next of kin email address"
              />
              {errors.next_of_kin_email && <div className="form-error">{errors.next_of_kin_email}</div>}
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="save-button"
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ProfileScreen
